import { payload, PayloadStates } from '@lore/utils';
import payloadToModel from '../utils/payloadToModel';

/**
 * Update Blueprint — Dynamic variant
 *
 * Use this when the target payload is not known at hook creation time and must
 * be supplied at mutation call time instead.
 *
 * Contrast with `update.js` (the static variant):
 *
 *   Static  — payload bound at hook creation:
 *     const mutation = useQueryMutation('tagNode.update', specificRecord);
 *     mutation.mutateAsync(partialData);   // merges partialData into specificRecord
 *
 *   Dynamic — payload passed at call time:
 *     const mutation = useQueryMutation('tagNode.updateDynamic');
 *     mutation.mutateAsync(fullPayload);   // fullPayload IS the record to update
 *
 * This is useful when a single mutation instance (e.g. in a context provider)
 * needs to update different records depending on runtime conditions, such as
 * iterating over a list and saving each item individually.
 *
 * @param {string} key - The mutation key (e.g. 'tagNode.updateDynamic')
 * @param {object} options
 * @param {Function} options.Model - The lore model constructor
 * @param {string} options.modelName - The model name used for cache keys
 * @param {object} options.queryClient - The react-query QueryClient instance
 */
export default function(key, { Model, modelName, queryClient }) {
  return {
    /**
     * Receives the complete payload (the record to update) at call time.
     * @param {object} currentPayload - Full payload including id and updated data
     */
    mutationFn: async function(currentPayload) {
      const nextModel = payloadToModel(currentPayload, Model);

      try {
        await nextModel.save();

        return payload(
          nextModel,
          PayloadStates.RESOLVED
        );
      } catch (err) {
        const response = err.response;
        const error = response.data;
        throw payload(nextModel, PayloadStates.ERROR_UPDATING, error);
      }
    },

    /** Optimistically marks the record as UPDATING in the cache. */
    onMutate: async function(currentPayload) {
      const optimisticModel = payloadToModel(currentPayload, Model);
      const optimisticPayload = payload(optimisticModel, PayloadStates.UPDATING);

      queryClient.setQueryData([modelName, 'byId', currentPayload.id], optimisticPayload);

      return {
        previousPayload: currentPayload,
        optimisticPayload
      };
    },

    /** Writes the resolved result back to the cache on success. */
    onSuccess: async function(result, variables, context) {
      queryClient.setQueryData([modelName, 'byId', context?.previousPayload?.id], result);

      if (modelName === 'tagNode') {
        await queryClient.invalidateQueries(['currentUser']).catch(() => {});
      }
    },

    /** Rolls back to the original payload on error. */
    onError: function(error, variables, context) {
      if (context?.previousPayload?.id) {
        queryClient.setQueryData([modelName, 'byId', context.previousPayload.id], context.previousPayload);
      }
    },
  };
}
